import type { ComboboxItem } from '@/components'
import { Combobox } from '@/components'
import { useTranslate } from '@ur-solutions/react-hooks'
import React from 'react'
import type { ShallowCategory } from '.'
import { CategorySelect } from '.'
import type { UseIssueFieldConfig } from '../field-config'
import { IssueFormField } from '../field-config'

interface CreateIssueCategoryCardProps {
  category: string | null
  subcategory: string | null
  subcategoryOptions: ComboboxItem<string>[]
  categoryError?: string | null
  subcategoryError?: string | null
  fieldConfig: UseIssueFieldConfig
  onChangeCategory(id: string, category: ShallowCategory | null): void
  onChangeSubcategory(id: string | null): void
}

/**
 * The category picker, at the head of the create form rather than in the
 * metadata rail: the category decides which fields the rest of the form shows
 * and requires, so it has to be answered before anything below it makes
 * sense. Subcategory rides along because it is meaningless without one.
 */
export const CreateIssueCategoryCard: React.FC<
  CreateIssueCategoryCardProps
> = ({
  category,
  subcategory,
  subcategoryOptions,
  categoryError,
  subcategoryError,
  fieldConfig,
  onChangeCategory,
  onChangeSubcategory,
}) => {
  const translations = useTranslate({
    category: 'common.category',
    subCategory: 'common.sub-category',
    select: 'common.select',
    help: 'issues.category-decides-form',
  })

  const showSubcategory =
    !!category &&
    subcategoryOptions.length > 0 &&
    fieldConfig.isVisible(IssueFormField.SUBCATEGORY, 'create')

  return (
    <section className="mb-4 rounded-2xl bg-white p-5 shadow-card">
      <div className="grid max-w-3xl gap-4 md:grid-cols-2">
        <div>
          <div className="mb-1.5 flex items-center gap-1 text-xs font-semibold uppercase text-stone-500">
            {translations.category}
            <span className="text-rose-500">*</span>
          </div>
          <CategorySelect
            value={category}
            error={categoryError}
            placeholder={translations.select}
            onChange={onChangeCategory}
          />
          <p className="mt-1.5 text-xs text-stone-500">{translations.help}</p>
        </div>

        {showSubcategory && (
          <div>
            <div className="mb-1.5 flex items-center gap-1 text-xs font-semibold uppercase text-stone-500">
              {fieldConfig.labelFor(
                IssueFormField.SUBCATEGORY,
                translations.subCategory
              )}
              {fieldConfig.isRequired(IssueFormField.SUBCATEGORY, 'create') && (
                <span className="text-rose-500">*</span>
              )}
            </div>
            <Combobox<string>
              mode="single"
              value={subcategory ?? ''}
              items={subcategoryOptions}
              clearable
              onChange={value => onChangeSubcategory(value || null)}
            />
            {subcategoryError && (
              <p className="mt-1 text-xs text-rose-500">{subcategoryError}</p>
            )}
          </div>
        )}
      </div>
    </section>
  )
}
